import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import EmailEditor from "react-email-editor";
import axios from "axios";
import config from "../Config/config";

import IntakeForm from "../Common/IntakeForm";
import MarketingAudienceStep from "./MarketingAudienceStep";

import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

const EMAIL_REGEX = /^\S+@\S+\.\S+$/;
const normalizeEmail = (email) => String(email || "").trim().toLowerCase();
const hasValidEmail = (email) => EMAIL_REGEX.test(normalizeEmail(email));
const normalizeUrl = (u) => String(u || "").trim();
const MARKETING_FROM_EMAIL = "jeff@auctionfuel.com";

const deriveContactNames = (contact = {}) => {
  const first = String(contact?.firstName || "").trim();
  const last = String(contact?.lastName || "").trim();
  const full = String(contact?.name || "").trim();

  if (first || last) {
    const name = String([first, last].filter(Boolean).join(" ")).trim() || full || "";
    return { firstName: first, lastName: last, name };
  }

  if (!full) {
    return { firstName: "", lastName: "", name: "" };
  }

  const parts = full.split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
    name: full,
  };
};

const normalizeContact = (c) => {
  const email = normalizeEmail(c?.email);
  const rawSent = c?.emailSent;
  const emailSent =
    rawSent === true ||
    rawSent === 1 ||
    rawSent === "1" ||
    String(rawSent).toLowerCase() === "true";
  const names = deriveContactNames(c || {});
  return {
    ...c,
    email,
    emailSent,
    firstName: names.firstName,
    lastName: names.lastName,
    name: names.name,
  };
};

const getHasExplorerAccount = (contact) => {
  const a = contact?.emailAnalytics || {};
  return (contact?.hasExplorerAccount ?? a?.hasExplorerAccount) === true;
};

const formatShortDateTime = (ms) => {
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const toMillisFromUnknownTime = (value) => {
  if (!value) return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 1e12 ? value : value * 1000;
  }
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  const sec = value?.seconds ?? value?._seconds;
  if (typeof sec === "number" && Number.isFinite(sec)) return sec * 1000;
  if (typeof value?.toMillis === "function") {
    try {
      return value.toMillis();
    } catch {
      return null;
    }
  }
  return null;
};

const getLastSentDay = (contact) => {
  const v = contact?.lastSentDate || contact?.lastSentAt || contact?.lastSent;
  if (!v) return "";
  if (typeof v === "string") {
    const ms = Date.parse(v);
    if (!Number.isNaN(ms)) return new Date(ms).toISOString().slice(0, 10);
    return "";
  }
  const sec = v?.seconds ?? v?._seconds;
  if (typeof sec === "number" && Number.isFinite(sec)) return new Date(sec * 1000).toISOString().slice(0, 10);
  if (typeof v === "number" && Number.isFinite(v)) return new Date(v).toISOString().slice(0, 10);
  return "";
};

const buildPreviewFallbackHtml = () => `<!doctype html>
<html>
  <head><meta charset="utf-8" /><meta name="viewport" content="width=device-width,initial-scale=1"/></head>
  <body style="font-family:Arial;padding:24px;background:#f6f8fa;color:#111">
    <h3 style="margin:0 0 8px 0;">No exported email HTML yet</h3>
    <p style="margin:0;color:#444">Go to Content → Edit and click Preview to generate HTML.</p>
  </body>
</html>`;

const MarketingEmail = () => {
  const today = new Date().toISOString().split("T")[0];
  const [userId, setUserId] = useState(() => localStorage.getItem("userId") || "");
  const [subscriptionId, setSubscriptionId] = useState(() => localStorage.getItem("subscriptionId") || "");

  const [flowStep, setFlowStep] = useState(0);

  const [releaseDate, setReleaseDate] = useState(today);

  const [subject, setSubject] = useState("A Message from Listernaut");
  const [fromName, setFromName] = useState("Listernaut");
  const fromEmail = MARKETING_FROM_EMAIL;
  const [unlayerDesign, setUnlayerDesign] = useState(null);
  const [exportedHtml, setExportedHtml] = useState("");

  const [templateSyncing, setTemplateSyncing] = useState(false);
  const [editorMode, setEditorMode] = useState("editor");

  const [builderReady, setBuilderReady] = useState(false);
  const [builderStatusMsg, setBuilderStatusMsg] = useState("");
  const [builderMountKey, setBuilderMountKey] = useState(0);
  const builderLoadTimeoutRef = useRef(null);
  const builderPollRef = useRef(null);

  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState({ sent: 0, total: 0 });
  const [errorLog, setErrorLog] = useState([]);
  const [skipNotes, setSkipNotes] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const SEND_CHUNK_SIZE = 400;
  const SEND_CONCURRENCY = 3;

  const [refetching, setRefetching] = useState(false);
  const [refetchError, setRefetchError] = useState("");

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");
  const [creatingCategory, setCreatingCategory] = useState(false);

  const [unsentContacts, setUnsentContacts] = useState([]);
  const [sentContacts, setSentContacts] = useState([]);
  const [noEmailContacts, setNoEmailContacts] = useState([]);

  const [selectedContacts, setSelectedContacts] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [showNoEmail, setShowNoEmail] = useState(false);
  const [selectCount, setSelectCount] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sentDateFilter, setSentDateFilter] = useState("");
  const [sentSignupFilter, setSentSignupFilter] = useState("all");
  const [analyticsFilter, setAnalyticsFilter] = useState("all");
  const [audienceSubjectFilter, setAudienceSubjectFilter] = useState("all");
  const [audienceLinkFilter, setAudienceLinkFilter] = useState("all");
  const [audienceClickFilterOptions, setAudienceClickFilterOptions] = useState({ subjects: [], links: [] });
  const [audienceClickFilterRows, setAudienceClickFilterRows] = useState([]);
  const [audienceClickFilterBusy, setAudienceClickFilterBusy] = useState(false);
  const [audienceClickFilterError, setAudienceClickFilterError] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [editRow, setEditRow] = useState(null);
  const [editField, setEditField] = useState("");
  const [editValue, setEditValue] = useState("");
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [activeContact, setActiveContact] = useState(null);
  const [newNote, setNewNote] = useState("");

  const [intakeModalOpen, setIntakeModalOpen] = useState(false);
  const [intakeEmail, setIntakeEmail] = useState("");

  const [userClicksOpen, setUserClicksOpen] = useState(false);
  const [userClicksBusy, setUserClicksBusy] = useState(false);
  const [userClicksError, setUserClicksError] = useState("");
  const [userClicksRows, setUserClicksRows] = useState([]);
  const [userClicksFilterOptions, setUserClicksFilterOptions] = useState({ subjects: [], links: [] });
  const [userClicksSubjectFilter, setUserClicksSubjectFilter] = useState("all");
  const [userClicksLinkFilter, setUserClicksLinkFilter] = useState("all");
  const [userClicksEmailSearch, setUserClicksEmailSearch] = useState("");
  const [userClicksExpandedRows, setUserClicksExpandedRows] = useState({});
  const [userClickEventsByRow, setUserClickEventsByRow] = useState({});
  const [userClickEventsBusyByRow, setUserClickEventsBusyByRow] = useState({});

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templateNameInput, setTemplateNameInput] = useState("");
  const [templatesBusy, setTemplatesBusy] = useState(false);
  const [createTplOpen, setCreateTplOpen] = useState(false);
  const [createTplName, setCreateTplName] = useState("");
  const [createTplBaseId, setCreateTplBaseId] = useState(""); // optional base template
  const [tplBusy, setTplBusy] = useState(false);
  const emailEditorRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState("");

  useEffect(() => {
    const uid = localStorage.getItem("userId") || "";
    if (uid && uid !== userId) setUserId(uid);
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${config.API_BASE_URL}/get-user-details?userId=${encodeURIComponent(userId)}`);
        const customSubObj =
          data?.customSubscription && typeof data.customSubscription === "object" ? data.customSubscription : null;
        let subFromUser = "";
        if (customSubObj) {
          const entries = Object.entries(customSubObj)
            .map(([, v]) => String(v || "").trim())
            .filter(Boolean);
          if (entries.length) subFromUser = entries[entries.length - 1];
        }
        const nextSubId = String(subFromUser || data?.subscriptionId || localStorage.getItem("subscriptionId") || "").trim();
        if (!cancelled && nextSubId) {
          setSubscriptionId(nextSubId);
          localStorage.setItem("subscriptionId", nextSubId);
        }
      } catch {
        // best effort only; fallback to localStorage subscriptionId
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const orgHeaders = useMemo(() => {
    const h = {};
    if (userId) h["x-user-id"] = userId;
    if (subscriptionId) h["x-subscription-id"] = subscriptionId;
    return h;
  }, [userId, subscriptionId]);

  const withOrgHeaders = (cfg = {}) => ({
    ...cfg,
    headers: { ...(orgHeaders || {}), ...(cfg?.headers || {}) },
  });

  useEffect(() => {
    if (flowStep === 1 && editorMode === "editor" && !builderReady) {
      const t = setTimeout(() => {
        if (!builderReady) setBuilderMountKey((k) => k + 1);
      }, 1500);
      return () => clearTimeout(t);
    }
  }, [flowStep, editorMode, builderReady]);

  const clearBuilderTimers = () => {
    if (builderLoadTimeoutRef.current) {
      clearTimeout(builderLoadTimeoutRef.current);
      builderLoadTimeoutRef.current = null;
    }
    if (builderPollRef.current) {
      clearInterval(builderPollRef.current);
      builderPollRef.current = null;
    }
  };

  const skipDesignAutoSaveRef = useRef(false);

  const loadBuilderDesignIntoEditor = (design) => {
    const editor = emailEditorRef.current?.editor;
    if (!editor || typeof editor.loadDesign !== "function") return;
    if (!design) return;
    try {
      skipDesignAutoSaveRef.current = true;
      editor.loadDesign(design);
      setTimeout(() => { skipDesignAutoSaveRef.current = false; }, 3000);
    } catch (e) {
      console.error("Failed to load Unlayer design:", e);
      skipDesignAutoSaveRef.current = false;
    }
  };

  const markBuilderReady = () => {
    setBuilderReady(true);
    setBuilderStatusMsg("");
    clearBuilderTimers();
    if (unlayerDesign) {
      setTimeout(() => loadBuilderDesignIntoEditor(unlayerDesign), 0);
    }
  };

  const handleEditorLoad = () => {
    setBuilderReady(false);
    setBuilderStatusMsg("Loading email builder…");
    clearBuilderTimers();

    builderPollRef.current = setInterval(() => {
      const editor = emailEditorRef.current?.editor;
      if (editor && typeof editor.exportHtml === "function" && typeof editor.loadDesign === "function") {
        markBuilderReady();
      }
    }, 250);

    builderLoadTimeoutRef.current = setTimeout(() => {
      const editor = emailEditorRef.current?.editor;
      if (editor && typeof editor.exportHtml === "function") {
        markBuilderReady();
        return;
      }
      setBuilderReady(false);
      setBuilderStatusMsg("Email builder failed to initialize. Click Reload builder.");
      clearBuilderTimers();
    }, 12000);
  };

  const handleEditorReady = () => {
    markBuilderReady();
  };

  const autoSaveToDb = useCallback(async () => {
    const tplId = selectedTemplateId;
    if (!tplId) return;

    const editor = emailEditorRef.current?.editor;
    let design = unlayerDesign;
    let html = exportedHtml;

    if (editor && typeof editor.exportHtml === "function") {
      const data = await new Promise((resolve) => editor.exportHtml((d) => resolve(d)));
      const freshDesign = data?.design ?? null;
      const freshHtml = String(data?.html || "");
      // Only use fresh export if it has real content (not just skeleton/boilerplate)
      const hasRealContent = freshHtml.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim().length > 50;
      if (hasRealContent) {
        design = freshDesign ?? design;
        html = freshHtml;
        if (freshDesign) setUnlayerDesign(freshDesign);
        setExportedHtml(freshHtml);
      }
    }

    // Don't save to DB if HTML has no real content
    const textContent = String(html || "").replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
    if (textContent.length < 50) {
      setAutoSaveStatus("");
      return;
    }

    setAutoSaveStatus("saving");
    try {
      const payload = {
        name: templateNameInput.trim() || "Untitled",
        subject: String(subject || "").trim() || "A Message from Listernaut",
        fromName,
        unlayerDesign: design,
        html,
      };
      await axios.put(`${config.API_BASE_URL}/email-templates/${tplId}`, payload, withOrgHeaders());
      setAutoSaveStatus("saved");
      setTimeout(() => setAutoSaveStatus(""), 2000);
    } catch (err) {
      console.error("Auto-save failed:", err);
      setAutoSaveStatus("error");
      setTimeout(() => setAutoSaveStatus(""), 3000);
    }
  }, [selectedTemplateId, templateNameInput, subject, fromName, unlayerDesign, exportedHtml]);

  const triggerAutoSave = useCallback(() => {
    if (!selectedTemplateId) return;
    setAutoSaveStatus("pending");
    clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => autoSaveToDb(), 2000);
  }, [selectedTemplateId, autoSaveToDb]);

  useEffect(() => {
    if (!builderReady) return;
    const editor = emailEditorRef.current?.editor;
    if (!editor || typeof editor.addEventListener !== "function") return;

    const onDesignUpdated = () => {
      if (skipDesignAutoSaveRef.current) return;
      triggerAutoSave();
    };

    editor.addEventListener("design:updated", onDesignUpdated);
    return () => {
      clearTimeout(autoSaveTimerRef.current);
      if (typeof editor.removeEventListener === "function") {
        editor.removeEventListener("design:updated", onDesignUpdated);
      }
    };
  }, [builderReady, triggerAutoSave]);

  const skipAutoSaveRef = useRef(false);
  useEffect(() => {
    if (!selectedTemplateId) return;
    if (skipAutoSaveRef.current) { skipAutoSaveRef.current = false; return; }
    triggerAutoSave();
  }, [subject, fromName, templateNameInput]);

  const waitForBuilderReady = async (timeoutMs = 15000) => {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      if (builderReady) return true;
      await new Promise((r) => setTimeout(r, 120));
    }
    return false;
  };

  const exportBuilder = async () => {
    const editor = emailEditorRef.current?.editor;
    if (!editor || typeof editor.exportHtml !== "function") {
      throw new Error("Email builder is not ready yet. Switch to Edit and wait for it to load.");
    }

    const data = await new Promise((resolve) => editor.exportHtml((d) => resolve(d)));
    const design = data?.design ?? null;
    const html = data?.html ?? "";

    setUnlayerDesign(design || null);
    setExportedHtml(String(html || ""));

    return { design: design || null, html: String(html || "") };
  };

  const ensureExportFresh = async () => {
    if (flowStep !== 1) setFlowStep(1);

    if (editorMode !== "editor") {
      setEditorMode("editor");
      await new Promise((r) => setTimeout(r, 0));
    }

    if (!builderReady) {
      setBuilderMountKey((k) => k + 1);
      const ok = await waitForBuilderReady(15000);
      if (!ok) {
        throw new Error("Email builder is not ready yet. Click Reload builder and try again.");
      }
    }

    const { design, html } = await exportBuilder();
    if (!design || typeof design !== "object") {
      throw new Error("Unlayer design export returned empty. Try Reload builder, then Preview once, then save.");
    }
    if (!html || String(html).trim().length < 20) {
      throw new Error("Exported HTML looks empty. Try Preview once, then save.");
    }
    return { design, html };
  };

  const EMPTY_UNLAYER_DESIGN = {
    body: {
      rows: [],
      values: {
        backgroundColor: "#ffffff",
        contentWidth: "600px",
        fontFamily: { label: "Arial", value: "arial,helvetica,sans-serif" },
      },
    },
  };
  const fetchCategoriesFresh = async () => {
    const catRes = await axios.get(`${config.API_BASE_URL}/categories`, withOrgHeaders());
    if (Array.isArray(catRes.data)) {
      const list = catRes.data
        .map((c) => ({
          id: c.id,
          name: c.name,
          contactCount: Number(c.contactCount || 0),
          contactEmailCount: Number(c.contactEmailCount || 0),
          emailsSentCount: Number(c.emailsSentCount || 0),
          totalSignups: Number(c.totalSignups || 0),
        }))
        .filter((c) => c.name)
        .sort((a, b) => String(a.name).localeCompare(String(b.name)));
      setCategories(list);
      return list;
    }
    setCategories([]);
    return [];
  };

  const handleCreateCategory = async () => {
    const name = String(newCategoryName || "").trim();
    if (!name) return alert("Enter a category name.");

    setCreatingCategory(true);
    try {
      let created = null;
      try {
        const res = await axios.post(`${config.API_BASE_URL}/categories`, { name }, withOrgHeaders());
        created = res?.data || null;
      } catch (e) {
        const res2 = await axios.post(`${config.API_BASE_URL}/create-category`, { name }, withOrgHeaders());
        created = res2?.data || null;
      }

      const fresh = await fetchCategoriesFresh();

      const matchById = created?.id ? (fresh || []).find((c) => c.id === created.id) : null;
      const matchByName = (fresh || []).find((c) => String(c.name).trim().toLowerCase() === name.toLowerCase());

      const cat = matchById || matchByName;
      if (cat) {
        await loadContactsForCategory(cat);
      } else {
        setTimeout(() => {
          const cat2 = (fresh || []).find((c) => String(c.name).trim().toLowerCase() === name.toLowerCase());
          if (cat2) loadContactsForCategory(cat2);
        }, 50);
      }

      setNewCategoryName("");
    } catch (err) {
      console.error("Failed to create category:", err);
      const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Unknown error";
      alert(`Failed to create category: ${msg}`);
    } finally {
      setCreatingCategory(false);
    }
  };

  const loadContactsForCategory = async (cat, opts = {}) => {
    const categoryName = cat?.name || "";
    const categoryId = cat?.id || "";

    setAudienceSubjectFilter("all");
    setAudienceLinkFilter("all");
    setAudienceClickFilterOptions({ subjects: [], links: [] });
    setAudienceClickFilterRows([]);
    setAudienceClickFilterError("");

    setSelectedCategory(categoryName);
    setSelectedCategoryId(categoryId);

    setUnsentContacts([]);
    setSentContacts([]);
    setNoEmailContacts([]);
    setSelectedContacts({});
    setPage(0);

    setEditRow(null);
    setEditField("");
    setEditValue("");

    if (!categoryName) return;
    const effectiveShowNoEmail = typeof opts.showNoEmail === "boolean" ? opts.showNoEmail : showNoEmail;

    try {
      const params = new URLSearchParams();
      params.set("all", "1");
      const res = await axios.get(
        `${config.API_BASE_URL.replace(/\/api$/, "")}/contacts/${encodeURIComponent(categoryName)}?${params.toString()}`,
        withOrgHeaders()
      );

      const payload = res.data;
      const list = Array.isArray(payload) ? payload : Array.isArray(payload?.contacts) ? payload.contacts : [];
      const normalized = list.map(normalizeContact);

      const unsent = normalized.filter((c) => hasValidEmail(c.email) && !c.emailSent);
      const sent = normalized.filter((c) => hasValidEmail(c.email) && c.emailSent);
      const noEmail = normalized.filter((c) => !hasValidEmail(c.email));

      setUnsentContacts(unsent);
      setSentContacts(sent);
      setNoEmailContacts(noEmail);

      if (effectiveShowNoEmail) {
      }
    } catch (err) {
      console.error("Failed to load contacts for category:", err);
    }
  };

  const refetchDetails = async () => {
    setRefetchError("");
    setRefetching(true);
    try {
      await fetchCategoriesFresh();
      if (selectedCategoryId && selectedCategory) {
        await loadContactsForCategory({ id: selectedCategoryId, name: selectedCategory }, { showNoEmail });
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to refetch";
      setRefetchError(String(msg));
    } finally {
      setRefetching(false);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const res = await axios.get(`${config.API_BASE_URL}/email-templates`, withOrgHeaders());
      if (Array.isArray(res.data)) setTemplates(res.data);
    } catch (err) {
      console.error("Failed to fetch email templates:", err);
    }
  };

  const buildTemplatePayloadFromState = (design, html) => ({
    name: templateNameInput.trim(),
    subject: String(subject || "").trim() || "A Message from Listernaut",
    fromName,
    unlayerDesign: design ?? unlayerDesign ?? null,
    html: String(html ?? exportedHtml ?? ""),
  });

  const handleSelectTemplate = async (e) => {
    const id = e.target.value;
    setSelectedTemplateId(id);

    if (!id) return;

    const t = templates.find((x) => x.id === id);
    if (!t) return;

    console.log("[MarketingEmail] Template selected:", {
      id: t.id,
      name: t.name,
      subject: t.subject,
      htmlLength: String(t.html || "").length,
      htmlPreview: String(t.html || "").slice(0, 500),
      hasUnlayerDesign: !!t.unlayerDesign,
    });

    skipAutoSaveRef.current = true;
    setTemplateNameInput(t.name || "");
    setSubject(t.subject || "A Message from Listernaut");
    setFromName(t.fromName || "Listernaut");

    setUnlayerDesign(t.unlayerDesign || null);
    setExportedHtml(t.html || "");

    setFlowStep(1);
    setEditorMode("editor");

    // ensure builder mounted then load, then re-export fresh HTML from design
    setTimeout(async () => {
      if (!builderReady) {
        setBuilderMountKey((k) => k + 1);
        await waitForBuilderReady(15000);
      }
      if (t.unlayerDesign) {
        loadBuilderDesignIntoEditor(t.unlayerDesign);
        setTemplateSyncing(true);
        setTimeout(async () => {
          try {
            const editor = emailEditorRef.current?.editor;
            if (!editor || typeof editor.exportHtml !== "function") return;
            const data = await new Promise((resolve) => editor.exportHtml((d) => resolve(d)));
            const freshHtml = String(data?.html || "");
            const textContent = freshHtml.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
            if (textContent.length > 50) {
              setExportedHtml(freshHtml);
              if (data?.design) setUnlayerDesign(data.design);
              const payload = {
                name: t.name || "Untitled",
                subject: String(t.subject || "").trim() || "A Message from Listernaut",
                fromName: t.fromName || "Listernaut",
                unlayerDesign: data?.design || t.unlayerDesign,
                html: freshHtml,
              };
              await axios.put(`${config.API_BASE_URL}/email-templates/${id}`, payload, withOrgHeaders());
            }
          } catch (e) {
            console.warn("[MarketingEmail] Sync failed:", e);
          } finally {
            setTemplateSyncing(false);
          }
        }, 4000);
      }
    }, 0);
  };
  const handleNewTemplate = async () => {
    setSelectedTemplateId("");
    setTemplateNameInput("");
    if (flowStep !== 1) setFlowStep(1);
    setEditorMode("editor");
  };

  const handleSaveNewTemplate = async () => {
    if (!templateNameInput.trim()) return alert("Please enter a template name.");
    setTemplatesBusy(true);
    try {
      const { design, html } = await ensureExportFresh();
      const payload = buildTemplatePayloadFromState(design, html);
      const res = await axios.post(`${config.API_BASE_URL}/email-templates`, payload, withOrgHeaders());
      await fetchEmailTemplates();
      if (res.data && res.data.id) setSelectedTemplateId(res.data.id);
      alert("Template saved.");
    } catch (err) {
      console.error("Failed to save template:", err);
      const msg = err?.response?.data?.message || err?.message || "Unknown error";
      alert(`Failed to save template: ${msg}`);
    } finally {
      setTemplatesBusy(false);
    }
  };
  const handleUpdateTemplate = async () => {
    if (!selectedTemplateId) return alert("Select a template to update.");

    setTplBusy(true);
    try {
      const { design, html } = await ensureExportFresh();
      const payload = {
        name: templateNameInput.trim() || "Untitled",
        subject: String(subject || "").trim() || "A Message from Listernaut",
        fromName,
        unlayerDesign: design,
        html,
      };
      await axios.put(`${config.API_BASE_URL}/email-templates/${selectedTemplateId}`, payload, withOrgHeaders());
      await fetchEmailTemplates();
      alert("Template updated.");
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Unknown error";
      alert(`Failed to update template: ${msg}`);
    } finally {
      setTplBusy(false);
    }
  };
  const handleDeleteTemplate = async () => {
    if (!selectedTemplateId) return alert("Select a template to delete.");
    const ok = window.confirm("Delete this template? This cannot be undone.");
    if (!ok) return;

    setTplBusy(true);
    try {
      await axios.delete(`${config.API_BASE_URL}/email-templates/${selectedTemplateId}`, withOrgHeaders());
      await fetchEmailTemplates();
      setSelectedTemplateId("");
      setTemplateNameInput("");
      alert("Template deleted.");
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Unknown error";
      alert(`Failed to delete template: ${msg}`);
    } finally {
      setTplBusy(false);
    }
  };
  const openCreateTemplateDialog = () => {
    setCreateTplName("");
    setCreateTplBaseId("");
    setCreateTplOpen(true);
  };
  const handleCreateTemplateConfirm = async () => {
    const name = String(createTplName || "").trim();
    if (!name) return alert("Enter a template name.");

    setTplBusy(true);
    try {
      let subjectForTemplate = String(subject || "").trim() || "A Message from Listernaut";
      let fromNameForTemplate = String(fromName || "").trim() || "Listernaut";

      // If they selected a base template, load that into state/editor first.
      // If they did NOT select a base, start from a blank design.
      if (createTplBaseId) {
        const base = templates.find((t) => t.id === createTplBaseId);
        if (!base) throw new Error("Base template not found.");

        subjectForTemplate = String(base.subject || "").trim() || "A Message from Listernaut";
        fromNameForTemplate = String(base.fromName || "").trim() || "Listernaut";

        setSubject(subjectForTemplate);
        setFromName(fromNameForTemplate);

        setUnlayerDesign(base.unlayerDesign || null);
        setExportedHtml(base.html || "");

        setFlowStep(1);
        setEditorMode("editor");

        if (!builderReady) {
          setBuilderMountKey((k) => k + 1);
          const ok = await waitForBuilderReady(15000);
          if (!ok) throw new Error("Email builder not ready. Try Reload builder.");
        }
        if (base.unlayerDesign) loadBuilderDesignIntoEditor(base.unlayerDesign);
      } else {
        // Blank template
        setSelectedTemplateId("");
        setTemplateNameInput("");
        setUnlayerDesign(EMPTY_UNLAYER_DESIGN);
        setExportedHtml("");

        setFlowStep(1);
        setEditorMode("editor");

        if (!builderReady) {
          setBuilderMountKey((k) => k + 1);
          const ok = await waitForBuilderReady(15000);
          if (!ok) throw new Error("Email builder not ready. Try Reload builder.");
        }
        loadBuilderDesignIntoEditor(EMPTY_UNLAYER_DESIGN);
      }

      // Export whatever is currently in editor (blank or loaded base)
      const { design, html } = await ensureExportFresh();

      const payload = {
        name,
        subject: subjectForTemplate,
        fromName: fromNameForTemplate,
        unlayerDesign: design,
        html,
      };

      const res = await axios.post(`${config.API_BASE_URL}/email-templates`, payload, withOrgHeaders());
      await fetchEmailTemplates();

      const newId = res?.data?.id;
      if (newId) setSelectedTemplateId(newId);
      setTemplateNameInput(name);
      setCreateTplName("");
      setCreateTplBaseId("");

      setCreateTplOpen(false);
      alert("Template created.");
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Unknown error";
      alert(`Failed to create template: ${msg}`);
    } finally {
      setTplBusy(false);
    }
  };
  const updateContactInState = (contactId, updates) => {
    setUnsentContacts((prev) => prev.map((c) => (c.id === contactId ? { ...c, ...updates } : c)));
    setSentContacts((prev) => prev.map((c) => (c.id === contactId ? { ...c, ...updates } : c)));
    setNoEmailContacts((prev) => prev.map((c) => (c.id === contactId ? { ...c, ...updates } : c)));
  };

  const startInlineEdit = (contactId, field, value) => {
    if (!selectedCategory) return;
    setEditRow(contactId);
    setEditField(field);
    setEditValue(value || "");
  };

  const saveInlineEdit = async () => {
    if (!editRow || !editField) return;

    try {
      await axios.post(`${config.API_BASE_URL}/update-contact`, {
        category: selectedCategory,
        contactId: editRow,
        updates: { [editField]: editValue },
      }, withOrgHeaders());

      updateContactInState(editRow, { [editField]: editValue });
    } catch (err) {
      console.error("Failed to save contact update:", err);
    }

    setEditRow(null);
    setEditField("");
    setEditValue("");
  };

  const openNotesDialog = (contact) => {
    setActiveContact(contact);
    setNewNote("");
    setNotesDialogOpen(true);
  };

  const saveNote = async () => {
    if (!newNote.trim() || !activeContact) return;

    const noteObj = { time: Date.now(), note: newNote.trim() };

    try {
      await axios.post(`${config.API_BASE_URL}/update-contact`, {
        category: selectedCategory,
        contactId: activeContact.id,
        updates: { notes: [noteObj] },
      }, withOrgHeaders());

      const updatedNotes = [...(activeContact.notes || []), noteObj];
      updateContactInState(activeContact.id, { notes: updatedNotes });

      setActiveContact((prev) => (prev ? { ...prev, notes: updatedNotes } : prev));
      setNewNote("");
    } catch (err) {
      console.error("Failed to save note:", err);
    }
  };

  const openIntakeModal = (email) => {
    setIntakeEmail(String(email || "").trim());
    setIntakeModalOpen(true);
  };
  const closeIntakeModal = () => {
    setIntakeModalOpen(false);
    setIntakeEmail("");
  };

  const handleDeleteCategory = async () => {
    if (!selectedCategoryId) return;
    const ok = window.confirm(
      `Delete category "${selectedCategory}"? This will delete ALL contacts in this category. This cannot be undone.`
    );
    if (!ok) return;

    try {
      await axios.delete(`${config.API_BASE_URL}/delete-category`, withOrgHeaders({ data: { categoryId: selectedCategoryId } }));

      await fetchCategoriesFresh();

      setSelectedCategoryId("");
      setSelectedCategory("");
      setUnsentContacts([]);
      setSentContacts([]);
      setNoEmailContacts([]);
      setSelectedContacts({});
      setPage(0);

      alert("Category deleted.");
    } catch (err) {
      console.error("Failed to delete category:", err);
      const msg = err?.response?.data?.message || err?.message || "Unknown error";
      alert(`Failed to delete category: ${msg}`);
    }
  };

  const handleDeleteContact = async (contact) => {
    if (!selectedCategoryId || !contact?.id) return;

    const label =
      contact.email ||
      contact.company ||
      [contact.firstName, contact.lastName].filter(Boolean).join(" ").trim() ||
      contact.name ||
      contact.username ||
      contact.id;
    const ok = window.confirm(`Delete contact "${label}"? This cannot be undone.`);
    if (!ok) return;

    try {
      await axios.delete(
        `${config.API_BASE_URL}/delete-contact`,
        withOrgHeaders({ data: { categoryId: selectedCategoryId, contactId: contact.id } })
      );

      setUnsentContacts((p) => p.filter((c) => c.id !== contact.id));
      setSentContacts((p) => p.filter((c) => c.id !== contact.id));
      setNoEmailContacts((p) => p.filter((c) => c.id !== contact.id));

      if (contact.email) {
        const selectedKey = normalizeEmail(contact.email);
        setSelectedContacts((prev) => {
          const next = { ...prev };
          delete next[selectedKey];
          return next;
        });
      }
    } catch (err) {
      console.error("Failed to delete contact:", err);
      const msg = err?.response?.data?.message || err?.message || "Unknown error";
      alert(`Failed to delete contact: ${msg}`);
    }
  };

  const toggleContact = (email) => {
    const key = normalizeEmail(email);
    if (!key) return;
    setSelectedContacts((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const matchesAnalyticsFilter = useCallback(
    (contact) => {
      if (!analyticsFilter || analyticsFilter === "all") return true;
      const a = contact?.emailAnalytics || {};
      const clicks = a?.clicks || {};
      switch (analyticsFilter) {
        case "processed":
          return !!a.processed;
        case "delivered":
          return !!a.delivered;
        case "opened":
          return !!a.opened;
        case "bounced":
          return !!a.bounced;
        case "clicked_explorer":
          return !!clicks.explorer;
        case "clicked_youtube":
          return !!clicks.youtube;
        case "clicked_meeting":
          return !!clicks.meeting;
        case "clicked_unsubscribe":
          return !!clicks.unsubscribe;
        default:
          return true;
      }
    },
    [analyticsFilter]
  );

  const hasAudienceClickFilters = useMemo(
    () =>
      (audienceSubjectFilter && audienceSubjectFilter !== "all") ||
      (audienceLinkFilter && audienceLinkFilter !== "all"),
    [audienceSubjectFilter, audienceLinkFilter]
  );

  const audienceClickedEmailSet = useMemo(() => {
    const next = new Set();
    for (const row of audienceClickFilterRows || []) {
      const email = normalizeEmail(row?.email);
      if (hasValidEmail(email)) next.add(email);
    }
    return next;
  }, [audienceClickFilterRows]);

  const matchesAudienceClickFilters = useCallback(
    (contact) => {
      if (!hasAudienceClickFilters) return true;
      const email = normalizeEmail(contact?.email);
      if (!hasValidEmail(email)) return false;
      return audienceClickedEmailSet.has(email);
    },
    [hasAudienceClickFilters, audienceClickedEmailSet]
  );

  const matchesSearchTerm = useCallback((contact, term) => {
    if (!term) return true;
    return (
      (contact.email || "").toLowerCase().includes(term) ||
      (contact.company || "").toLowerCase().includes(term) ||
      (contact.username || "").toLowerCase().includes(term) ||
      (contact.firstName || "").toLowerCase().includes(term) ||
      (contact.lastName || "").toLowerCase().includes(term) ||
      (contact.name || "").toLowerCase().includes(term) ||
      (contact.phone || "").toLowerCase().includes(term) ||
      (contact.address || "").toLowerCase().includes(term) ||
      (contact.website || "").toLowerCase().includes(term)
    );
  }, []);

  const matchesAudienceFilters = useCallback(
    (contact, term = (searchTerm || "").toLowerCase()) => {
      if (statusFilter === "sent" && !showNoEmail && sentDateFilter) {
        const day = getLastSentDay(contact);
        if (!day || day !== sentDateFilter) return false;
      }

      if (statusFilter === "sent" && !showNoEmail && sentSignupFilter && sentSignupFilter !== "all") {
        const has = getHasExplorerAccount(contact);
        if (sentSignupFilter === "signed" && !has) return false;
        if (sentSignupFilter === "not_signed" && has) return false;
      }

      if (!matchesAnalyticsFilter(contact)) return false;
      if (!matchesAudienceClickFilters(contact)) return false;
      return matchesSearchTerm(contact, term);
    },
    [
      searchTerm,
      statusFilter,
      showNoEmail,
      sentDateFilter,
      sentSignupFilter,
      matchesAnalyticsFilter,
      matchesAudienceClickFilters,
      matchesSearchTerm,
    ]
  );

  const currentWithEmailContacts = useMemo(() => {
    if (statusFilter === "sent") return sentContacts;
    if (statusFilter === "unsent") return unsentContacts;

    const merged = [...(unsentContacts || []), ...(sentContacts || [])].map(normalizeContact);
    const byEmail = new Map();
    for (const contact of merged) {
      const email = normalizeEmail(contact?.email);
      if (!hasValidEmail(email)) continue;
      const prev = byEmail.get(email);
      if (!prev) {
        byEmail.set(email, { ...contact, email });
        continue;
      }

      const prevSent = !!prev?.emailSent;
      const nextSent = !!contact?.emailSent;
      if (nextSent && !prevSent) {
        byEmail.set(email, { ...contact, email });
        continue;
      }

      if (nextSent === prevSent) {
        const prevMs =
          toMillisFromUnknownTime(prev?.lastSentDate || prev?.lastSentAt || prev?.lastSent) || 0;
        const nextMs =
          toMillisFromUnknownTime(contact?.lastSentDate || contact?.lastSentAt || contact?.lastSent) || 0;
        if (nextMs > prevMs) byEmail.set(email, { ...contact, email });
      }
    }
    return Array.from(byEmail.values());
  }, [statusFilter, unsentContacts, sentContacts]);

  const handleSelectCount = () => {
    const count = parseInt(selectCount, 10);
    if (!count || count <= 0) return alert("Enter a valid number");

    const currentList = showNoEmail ? noEmailContacts : currentWithEmailContacts;
    const term = (searchTerm || "").toLowerCase();
    const filtered = currentList.filter((c) => matchesAudienceFilters(c, term));

    const base = filtered.length ? filtered : currentList;
    const toSelect = base.slice(0, count);
    const newSel = {};
    toSelect.forEach((c) => {
      if (hasValidEmail(c.email)) newSel[c.email] = true;
    });
    setSelectedContacts(newSel);
  };

  const handleSelectAll = () => {
    const allSel = {};
    const currentList = showNoEmail ? noEmailContacts : currentWithEmailContacts;
    const term = (searchTerm || "").toLowerCase();

    const base = currentList.filter((c) => matchesAudienceFilters(c, term));

    base.forEach((c) => {
      if (hasValidEmail(c.email)) allSel[c.email] = true;
    });
    setSelectedContacts(allSel);
  };

  const handleDeselectAll = () => setSelectedContacts({});

  const selectedCategoryStats = useMemo(() => {
    if (!selectedCategoryId) return null;
    const cat = categories.find((c) => c.id === selectedCategoryId);
    if (!cat) return null;
    return {
      contactCount: Number(cat.contactCount || 0),
      contactEmailCount: Number(cat.contactEmailCount || 0),
      emailsSentCount: Number(cat.emailsSentCount || 0),
      totalSignups: Number(cat.totalSignups || 0),
    };
  }, [categories, selectedCategoryId]);

  const visibleContacts = useMemo(() => {
    const currentList = showNoEmail ? noEmailContacts : currentWithEmailContacts;
    const term = (searchTerm || "").toLowerCase();

    return currentList.filter((c) => matchesAudienceFilters(c, term));
  }, [
    showNoEmail,
    noEmailContacts,
    currentWithEmailContacts,
    searchTerm,
    matchesAudienceFilters,
  ]);

  useEffect(() => {
    if (!selectedCategoryId) {
      setAudienceClickFilterOptions({ subjects: [], links: [] });
      setAudienceClickFilterRows([]);
      setAudienceClickFilterError("");
      setAudienceClickFilterBusy(false);
      return;
    }

    let cancelled = false;

    const fetchAudienceClickFilters = async () => {
      setAudienceClickFilterBusy(true);
      setAudienceClickFilterError("");
      try {
        const params = {
          categoryId: selectedCategoryId,
          limit: 5000,
          scanLimit: 20000,
        };
        if (audienceSubjectFilter && audienceSubjectFilter !== "all") params.subject = audienceSubjectFilter;
        if (audienceLinkFilter && audienceLinkFilter !== "all") params.clickedLink = audienceLinkFilter;

        const { data } = await axios.get(`${config.API_BASE_URL}/user-clicks`, { params, headers: orgHeaders });
        if (cancelled) return;

        setAudienceClickFilterRows(Array.isArray(data?.rows) ? data.rows : []);
        setAudienceClickFilterOptions({
          subjects: Array.isArray(data?.filterOptions?.subjects) ? data.filterOptions.subjects : [],
          links: Array.isArray(data?.filterOptions?.links) ? data.filterOptions.links : [],
        });
      } catch (e) {
        if (cancelled) return;
        setAudienceClickFilterRows([]);
        setAudienceClickFilterOptions({ subjects: [], links: [] });
        setAudienceClickFilterError(e?.response?.data?.error || e?.message || "Failed to load subject and link filters.");
      } finally {
        if (!cancelled) setAudienceClickFilterBusy(false);
      }
    };

    fetchAudienceClickFilters();

    return () => {
      cancelled = true;
    };
  }, [selectedCategoryId, audienceSubjectFilter, audienceLinkFilter, orgHeaders]);

  const filteredUserClicksRows = useMemo(() => {
    const term = String(userClicksEmailSearch || "").trim().toLowerCase();
    if (!term) return userClicksRows;
    return (userClicksRows || []).filter((row) => String(row?.email || "").toLowerCase().includes(term));
  }, [userClicksRows, userClicksEmailSearch]);

  const fetchUserClicks = async () => {
    if (!selectedCategoryId) {
      setUserClicksRows([]);
      setUserClicksFilterOptions({ subjects: [], links: [] });
      return;
    }

    setUserClicksBusy(true);
    setUserClicksError("");
    try {
      const params = {
        categoryId: selectedCategoryId,
        limit: 1000,
      };
      if (userClicksSubjectFilter && userClicksSubjectFilter !== "all") params.subject = userClicksSubjectFilter;
      if (userClicksLinkFilter && userClicksLinkFilter !== "all") params.clickedLink = userClicksLinkFilter;

      const { data } = await axios.get(`${config.API_BASE_URL}/user-clicks`, withOrgHeaders({ params }));
      setUserClicksRows(Array.isArray(data?.rows) ? data.rows : []);
      setUserClicksFilterOptions({
        subjects: Array.isArray(data?.filterOptions?.subjects) ? data.filterOptions.subjects : [],
        links: Array.isArray(data?.filterOptions?.links) ? data.filterOptions.links : [],
      });
    } catch (e) {
      setUserClicksError(e?.response?.data?.error || e?.message || "Failed to load user clicks.");
      setUserClicksRows([]);
    } finally {
      setUserClicksBusy(false);
    }
  };

  const openUserClicksDialog = (prefillEmail = "") => {
    if (!selectedCategoryId) {
      alert("Please select a category first.");
      return;
    }
    setUserClicksOpen(true);
    setUserClicksError("");
    setUserClicksExpandedRows({});
    setUserClickEventsByRow({});
    setUserClickEventsBusyByRow({});
    setUserClicksEmailSearch(String(prefillEmail || "").trim().toLowerCase());
  };

  const closeUserClicksDialog = () => {
    setUserClicksOpen(false);
    setUserClicksExpandedRows({});
    setUserClickEventsByRow({});
    setUserClickEventsBusyByRow({});
  };

  const fetchUserClickEvents = async (rowId) => {
    if (!rowId) return;
    setUserClickEventsBusyByRow((prev) => ({ ...prev, [rowId]: true }));
    try {
      const params = { limit: 300 };
      if (userClicksSubjectFilter && userClicksSubjectFilter !== "all") params.subject = userClicksSubjectFilter;
      if (userClicksLinkFilter && userClicksLinkFilter !== "all") params.clickedLink = userClicksLinkFilter;
      const { data } = await axios.get(
        `${config.API_BASE_URL}/user-clicks/${encodeURIComponent(rowId)}/events`,
        withOrgHeaders({ params })
      );
      setUserClickEventsByRow((prev) => ({ ...prev, [rowId]: Array.isArray(data?.events) ? data.events : [] }));
    } catch {
      setUserClickEventsByRow((prev) => ({ ...prev, [rowId]: [] }));
    } finally {
      setUserClickEventsBusyByRow((prev) => ({ ...prev, [rowId]: false }));
    }
  };

  const toggleUserClicksRow = async (rowId) => {
    const willExpand = !userClicksExpandedRows[rowId];
    setUserClicksExpandedRows((prev) => ({ ...prev, [rowId]: willExpand }));
    if (willExpand && !Object.prototype.hasOwnProperty.call(userClickEventsByRow, rowId)) {
      await fetchUserClickEvents(rowId);
    }
  };

  useEffect(() => {
    if (!userClicksOpen) return;
    fetchUserClicks();
  }, [userClicksOpen, userClicksSubjectFilter, userClicksLinkFilter, selectedCategoryId]);

  useEffect(() => {
    if (!userClicksOpen) return;
    setUserClicksExpandedRows({});
    setUserClickEventsByRow({});
    setUserClickEventsBusyByRow({});
  }, [userClicksSubjectFilter, userClicksLinkFilter, userClicksOpen]);

  const selectedCount = useMemo(() => Object.values(selectedContacts).filter(Boolean).length || 0, [selectedContacts]);
  const sendProgressPct = useMemo(() => {
    if (!progress.total) return 0;
    const ratio = Math.min(progress.sent, progress.total) / progress.total;
    return Math.round(ratio * 100);
  }, [progress.sent, progress.total]);

  const currentPreview = useMemo(() => {
    return normalizeUrl(exportedHtml) ? exportedHtml : buildPreviewFallbackHtml();
  }, [exportedHtml]);

  const reviewChecklist = useMemo(() => {
    const issues = [];
    const warnings = [];

    if (!selectedCategoryId) issues.push("Choose an audience (category).");
    if (!subject.trim()) issues.push("Add an email subject.");
    if (!fromName.trim()) warnings.push("From name is empty.");
    if (!exportedHtml || String(exportedHtml).trim().length < 50) issues.push("Preview the email to export HTML.");

    const pool = showNoEmail ? [] : currentWithEmailContacts;
    const selected = pool.filter((c) => selectedContacts[c.email]);
    const toSend = selected.filter((c) => hasValidEmail(c?.email));

    if (showNoEmail) issues.push("You are viewing contacts without email. Switch to 'With Email' to send.");
    if (selectedCount === 0) issues.push("Select at least one recipient.");
    if (toSend.length === 0 && selectedCount > 0) issues.push("All selected recipients are invalid.");

    return { issues, warnings, toSendCount: toSend.length };
  }, [selectedCategoryId, subject, fromName, exportedHtml, selectedContacts, selectedCount, showNoEmail, currentWithEmailContacts]);

  const sendSelectedContacts = async () => {
    if (showNoEmail) {
      alert("This view contains contacts without a valid email. Switch to 'With Email' to send.");
      return;
    }

    // Always do a fresh export from the builder before sending
    let htmlToSend = exportedHtml;
    try {
      const fresh = await exportBuilder();
      if (fresh?.html && String(fresh.html).trim().length > 200) {
        htmlToSend = fresh.html;
      }
    } catch (e) {
      console.warn("Fresh export failed, using stored HTML:", e);
    }

    if (!htmlToSend || String(htmlToSend).trim().length < 50) {
      alert("No exported email HTML found. Go to Content → Preview to generate the HTML, then send.");
      return;
    }

    const pool = currentWithEmailContacts;
    const selected = pool.filter((c) => selectedContacts[c.email]);
    if (!selected.length) return alert("No contacts selected.");

    const dedupedByEmail = new Map();
    const skipped = [];
    selected.forEach((c) => {
      const normalizedEmail = normalizeEmail(c?.email);
      if (!hasValidEmail(normalizedEmail)) {
        skipped.push(`Skipping ${c?.email || "(no email)"} — invalid email.`);
        return;
      }
      if (dedupedByEmail.has(normalizedEmail)) {
        skipped.push(`Skipping ${normalizedEmail} — duplicate in selection.`);
        return;
      }
      dedupedByEmail.set(normalizedEmail, { ...c, email: normalizedEmail });
    });
    const toSend = Array.from(dedupedByEmail.values());

    if (!toSend.length) {
      setSkipNotes(skipped);
      alert("All selected contacts have invalid or missing email.");
      return;
    }

    console.log("[MarketingEmail] Prepared marketing send", {
      selectedTemplateId: selectedTemplateId || null,
      subject,
      fromName,
      category: selectedCategory,
      categoryId: selectedCategoryId,
      recipientCount: toSend.length,
      firstRecipients: toSend.slice(0, 10).map((c) => normalizeEmail(c?.email)),
      htmlLength: String(htmlToSend || "").length,
    });
    console.log("[MarketingEmail] HTML payload", htmlToSend);

    setSending(true);
    setErrorLog([]);
    setSkipNotes(skipped);
    setProgress({ sent: 0, total: toSend.length + skipped.length });

    const nowIso = new Date().toISOString();

    const chunk = (arr, size) => {
      const out = [];
      for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
      return out;
    };

    const chunks = chunk(toSend, SEND_CHUNK_SIZE);
    let successfulBatches = 0;
    let failedBatches = 0;
    const FALLBACK_UPDATE_CONCURRENCY = 25;

    const markBatchAsSent = (batch, lastSentDate) => {
      batch.forEach((c) => updateContactInState(c.id, { emailSent: true, lastSentDate }));

      setSelectedContacts((prev) => {
        const next = { ...prev };
        batch.forEach((c) => {
          if (c?.email) delete next[c.email];
        });
        return next;
      });
    };

    const patchContactsAsSent = async (batch, lastSentDate) => {
      try {
        await axios.patch(`${config.API_BASE_URL}/contacts/update-bulk`, {
          category: selectedCategory,
          categoryId: selectedCategoryId,
          emails: batch.map((c) => c.email),
          update: { emailSent: true, lastSentDate },
        }, withOrgHeaders());
        return;
      } catch (e) { }

      const queue = [...batch];
      const workers = Array.from({
        length: Math.min(FALLBACK_UPDATE_CONCURRENCY, queue.length),
      }).map(async () => {
        while (queue.length) {
          const c = queue.shift();
          if (!c) continue;
          try {
            await axios.patch(`${config.API_BASE_URL}/contacts/update`, {
              email: c.email,
              category: selectedCategory,
              update: { emailSent: true, lastSentDate },
            }, withOrgHeaders());
          } catch (patchErr) {
            const msg = patchErr?.response?.data?.error || patchErr?.message || "Failed to update contact";
            setErrorLog((prev) => [...prev, { email: c.email, error: `CONTACT_UPDATE: ${msg}` }]);
          }
        }
      });
      await Promise.all(workers);
    };

    const sendChunk = async (batch) => {
      const to = batch
        .map((c) => normalizeEmail(c?.email))
        .filter((email) => hasValidEmail(email));
      if (!to.length) {
        throw new Error("No valid recipients in batch after normalization.");
      }

      try {
        await axios.post(`${config.API_BASE_URL}/send-email`, {
          to,
          subject,
          html: htmlToSend,
          text: `Hi there,\n\nPlease view this email in HTML.`,
          fromName,
          releaseDate,
          customArgs: {
            listernaut_email_type: "marketing",
            marketing_category: selectedCategory,
            marketing_category_id: selectedCategoryId,
            marketing_category_name: selectedCategory,
            marketing_subject: subject,
            campaign_version: "v1",
          },
        }, withOrgHeaders());
      } catch (err) {
        const msg = err?.response?.data?.error || err?.message || "Unknown error";
        setErrorLog((prev) => [...prev, ...batch.map((c) => ({ email: c.email, error: `SEND: ${msg}` }))]);
        throw err;
      }
    };

    const runQueue = async () => {
      const queue = [...chunks];
      const workers = Array.from({ length: Math.min(SEND_CONCURRENCY, queue.length) }).map(async () => {
        while (queue.length) {
          const batch = queue.shift();
          if (!batch?.length) continue;

          try {
            await sendChunk(batch);
            await patchContactsAsSent(batch, nowIso);
            markBatchAsSent(batch, nowIso);
            successfulBatches += 1;
          } catch (e) {
            failedBatches += 1;
          } finally {
            setProgress((prev) => ({ ...prev, sent: Math.min(prev.sent + batch.length, prev.total) }));
          }
        }
      });

      await Promise.all(workers);
    };

    try {
      if (skipped.length) setProgress((prev) => ({ ...prev, sent: Math.min(prev.sent + skipped.length, prev.total) }));
      await runQueue();
      if (successfulBatches > 0 && failedBatches === 0) {
        setFlowStep(0);
      }
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    return () => {
      if (builderLoadTimeoutRef.current) clearTimeout(builderLoadTimeoutRef.current);
      if (builderPollRef.current) clearInterval(builderPollRef.current);
    };
  }, []);

  useEffect(() => {
    const fetchInit = async () => {
      try {
        await fetchCategoriesFresh();
      } catch (e) {
        console.error("Failed to fetch categories:", e);
      }
    };
    fetchInit();
  }, [subscriptionId]);

  useEffect(() => {
    fetchEmailTemplates();
  }, [subscriptionId]);

  const StepPill = ({ idx, title }) => {
    const active = flowStep === idx;
    const done = flowStep > idx;
    return (
      <button
        type="button"
        onClick={() => setFlowStep(idx)}
        className={`px-3 py-2 rounded-2xl border text-sm font-bold transition ${active
          ? "border-indigo-400 bg-indigo-50 text-[#333999]"
          : done
            ? "border-slate-200 bg-white/70 text-slate-700"
            : "border-slate-200 bg-white/50 text-slate-500"
          }`}
        title={title}
      >
        {done ? "✓ " : ""}
        {idx + 1}. {title}
      </button>
    );
  };

  return (
    <div className="ln-page-shell px-3 sm:px-6 py-4 sm:py-6 overflow-y-auto">
      {templateSyncing && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)",
        }}>
          <div style={{
            background: "#fff", borderRadius: 16, padding: "36px 48px",
            textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
            maxWidth: 360,
          }}>
            <div style={{ marginBottom: 16 }}>
              <svg width="48" height="48" viewBox="0 0 48 48" style={{ animation: "spin 1s linear infinite", margin: "0 auto" }}>
                <circle cx="24" cy="24" r="20" fill="none" stroke="#333999" strokeWidth="4" strokeDasharray="80 40" strokeLinecap="round" />
              </svg>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
            <p style={{ fontWeight: 800, fontSize: "1.1rem", color: "#333999", margin: "0 0 6px" }}>
              Syncing Template
            </p>
            <p style={{ color: "#64748b", fontSize: "0.85rem", margin: 0 }}>
              Please wait while the template is saved to the database...
            </p>
          </div>
        </div>
      )}
      <div className="w-full max-w-[1400px] mx-auto space-y-6">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-4xl font-extrabold text-[#333999]">Marketing Campaign</h1>
              <div className="section-divider mt-2" />
              <p className="text-xs text-slate-500 mt-2">Mailchimp-style flow: pick audience → design content → review & send.</p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={refetchDetails}
                disabled={refetching}
                className={`btn-secondary ${refetching ? "opacity-60 cursor-not-allowed" : ""}`}
                title="Reloads categories + contacts"
              >
                {refetching ? "Refetching…" : "Refetch details"}
              </button>

              <div className="flex flex-wrap items-center gap-2">
                <StepPill idx={0} title="Audience" />
                <StepPill idx={1} title="Content" />
                <StepPill idx={2} title="Review & Send" />
              </div>
            </div>
          </div>

          {refetchError ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{refetchError}</div>
          ) : null}
        </div>

        <div className="">
          <div className="lg:col-span-8 space-y-6">
            {flowStep === 0 && (
              <MarketingAudienceStep
                setFlowStep={setFlowStep}
                selectedCategoryId={selectedCategoryId}
                categories={categories}
                setSelectedCategoryId={setSelectedCategoryId}
                setSelectedCategory={setSelectedCategory}
                setUnsentContacts={setUnsentContacts}
                setSentContacts={setSentContacts}
                setNoEmailContacts={setNoEmailContacts}
                setSelectedContacts={setSelectedContacts}
                setPage={setPage}
                loadContactsForCategory={loadContactsForCategory}
                newCategoryName={newCategoryName}
                setNewCategoryName={setNewCategoryName}
                handleCreateCategory={handleCreateCategory}
                creatingCategory={creatingCategory}
                handleDeleteCategory={handleDeleteCategory}
                selectedCategoryStats={selectedCategoryStats}
                selectedCategory={selectedCategory}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                analyticsFilter={analyticsFilter}
                setAnalyticsFilter={setAnalyticsFilter}
                audienceSubjectFilter={audienceSubjectFilter}
                setAudienceSubjectFilter={setAudienceSubjectFilter}
                audienceLinkFilter={audienceLinkFilter}
                setAudienceLinkFilter={setAudienceLinkFilter}
                audienceClickFilterOptions={audienceClickFilterOptions}
                audienceClickFilterBusy={audienceClickFilterBusy}
                audienceClickFilterError={audienceClickFilterError}
                showNoEmail={showNoEmail}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                sentContacts={sentContacts}
                unsentContacts={unsentContacts}
                sentDateFilter={sentDateFilter}
                setSentDateFilter={setSentDateFilter}
                sentSignupFilter={sentSignupFilter}
                setSentSignupFilter={setSentSignupFilter}
                noEmailContacts={noEmailContacts}
                setShowNoEmail={setShowNoEmail}
                selectCount={selectCount}
                setSelectCount={setSelectCount}
                handleSelectCount={handleSelectCount}
                handleSelectAll={handleSelectAll}
                handleDeselectAll={handleDeselectAll}
                selectedCount={selectedCount}
                visibleContacts={visibleContacts}
                page={page}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                selectedContacts={selectedContacts}
                hasValidEmail={hasValidEmail}
                toggleContact={toggleContact}
                startInlineEdit={startInlineEdit}
                editRow={editRow}
                editField={editField}
                editValue={editValue}
                setEditValue={setEditValue}
                saveInlineEdit={saveInlineEdit}
                openNotesDialog={openNotesDialog}
                openIntakeModal={openIntakeModal}
                handleDeleteContact={handleDeleteContact}
                onOpenUserClicks={openUserClicksDialog}
              />
            )}

            {flowStep === 1 && (
              <div className="space-y-6">
                <div className="crd space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-wide text-[#333999]">Content</div>
                      <div className="text-xs text-slate-500 mt-1">Subject, sender, templates, and builder.</div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button type="button" className="btn-secondary" onClick={() => setFlowStep(0)}>
                        ← Back
                      </button>
                      <button type="button" className="btn-primary" onClick={() => setFlowStep(2)}>
                        Continue →
                      </button>
                    </div>
                  </div>

                  <div className="section-divider" />

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="section-label">Subject</label>
                      <input
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        className="w-full rounded-xl border border-slate-300/70 bg-white/90 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                        placeholder="Email subject"
                      />
                    </div>

                    <div>
                      <label className="section-label">Release Date</label>
                      <input
                        type="date"
                        value={releaseDate}
                        onChange={(e) => setReleaseDate(e.target.value)}
                        className="w-full rounded-xl border border-slate-300/70 bg-white/90 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                      />
                    </div>

                    <div>
                      <label className="section-label">From Name</label>
                      <input
                        value={fromName}
                        onChange={(e) => setFromName(e.target.value)}
                        className="w-full rounded-xl border border-slate-300/70 bg-white/90 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                        placeholder="Listernaut"
                      />
                      <p className="text-xs text-slate-500 mt-1">Emails send from {fromEmail}.</p>
                    </div>
                  </div>

                  {/* Templates */}
                  <div className="mt-2 pt-4 border-t border-slate-200">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-600">Templates</p>
                      <button
                        type="button"
                        className="btn-primary btn-sm"
                        onClick={openCreateTemplateDialog}
                        disabled={tplBusy}
                      >
                        + Create New Template…
                      </button>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="section-label">Existing Templates</label>
                        <select
                          className="w-full rounded-xl border border-slate-300/70 bg-white/90 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                          value={selectedTemplateId}
                          onChange={handleSelectTemplate}
                        >
                          <option value="">-- select a template --</option>
                          {templates.map((tpl) => (
                            <option key={tpl.id} value={tpl.id}>
                              {tpl.name}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-slate-500 mt-1">Selecting a template loads it into the editor.</p>
                      </div>

                      <div>
                        <label className="section-label">Selected Template Name</label>
                        <div className="mt-1 flex items-center gap-2">
                          <input
                            value={templateNameInput}
                            onChange={(e) => setTemplateNameInput(e.target.value)}
                            className="flex-1 rounded-xl border border-slate-300/70 bg-white/90 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                            placeholder="Template name"
                            disabled={!selectedTemplateId}
                          />

                          <button
                            type="button"
                            className={`btn-danger btn-sm ${!selectedTemplateId || tplBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                            disabled={!selectedTemplateId || tplBusy}
                            onClick={handleDeleteTemplate}
                          >
                            Delete
                          </button>
                        </div>

                        <div className="flex flex-wrap items-center gap-2 mt-2 min-h-[20px]">
                          {autoSaveStatus === "saving" ? (
                            <span className="text-xs text-slate-500 font-semibold">Saving…</span>
                          ) : autoSaveStatus === "saved" ? (
                            <span className="text-xs text-green-600 font-semibold">Saved</span>
                          ) : autoSaveStatus === "error" ? (
                            <span className="text-xs text-red-500 font-semibold">Save failed</span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="crd space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <h2 className="text-lg sm:text-xl font-extrabold text-[#333999]">Email Builder</h2>

                    <div className="flex flex-wrap items-center gap-2">
                      <div className="ln-toggle-group">
                        <button type="button" onClick={() => setEditorMode("editor")} className={`ln-toggle-pill ${editorMode === "editor" ? "ln-toggle-pill--active" : ""}`}>
                          Edit
                        </button>

                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await ensureExportFresh();
                            } catch (e) {
                              alert(e?.message || "Preview failed.");
                              return;
                            }
                            setEditorMode("preview");
                          }}
                          className={`ln-toggle-pill ${editorMode === "preview" ? "ln-toggle-pill--active" : ""}`}
                        >
                          Preview
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="section-divider" />

                  {editorMode === "editor" ? (
                    <div className="rounded-2xl overflow-hidden border border-slate-300/70 bg-white/90">
                      <div className="mb-2 flex items-center gap-2 px-2 pt-2">
                        {!builderReady ? (
                          <div className="text-xs text-slate-500">{builderStatusMsg || "Loading email builder…"}</div>
                        ) : autoSaveStatus === "saving" ? (
                          <div className="text-xs text-slate-500 font-semibold">Auto-saving…</div>
                        ) : autoSaveStatus === "saved" ? (
                          <div className="text-xs text-green-600 font-semibold">Auto-saved</div>
                        ) : autoSaveStatus === "error" ? (
                          <div className="text-xs text-red-500 font-semibold">Auto-save failed</div>
                        ) : (
                          <div className="text-xs text-slate-500">Email builder ready.</div>
                        )}
                        <div className="flex-1" />
                        {!builderReady ? (
                          <button
                            type="button"
                            className="btn-secondary btn-sm"
                            onClick={() => {
                              setBuilderStatusMsg("Reloading email builder…");
                              setBuilderReady(false);
                              setBuilderMountKey((k) => k + 1);
                            }}
                            title="Remount the email builder"
                          >
                            Reload builder
                          </button>
                        ) : null}
                      </div>

                      <div style={{ height: 720 }}>
                        <EmailEditor
                          key={builderMountKey}
                          ref={emailEditorRef}
                          onLoad={handleEditorLoad}
                          onReady={handleEditorReady}
                          minHeight="720px"
                        />
                      </div>
                      <Dialog
                        open={createTplOpen}
                        onClose={() => setCreateTplOpen(false)}
                        fullWidth
                        maxWidth="sm"
                        keepMounted={false}
                        sx={{
                          "& .MuiDialog-paper": {
                            margin: { xs: "16px", sm: "32px" },
                            borderRadius: "1.25rem",
                          },
                        }}
                      >
                        <DialogTitle sx={{ fontWeight: 900 }}>Create New Template</DialogTitle>

                        <DialogContent dividers sx={{ p: 2 }}>
                          <div className="space-y-4">
                            <div>
                              <label className="section-label">Template Name</label>
                              <input
                                value={createTplName}
                                onChange={(e) => setCreateTplName(e.target.value)}
                                className="w-full rounded-xl border border-slate-300/70 bg-white/90 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                                placeholder="e.g. Spring Promo v1"
                              />
                              <p className="text-xs text-slate-500 mt-1">This will save a new template.</p>
                            </div>

                            <div>
                              <label className="section-label">Start From Existing Template (optional)</label>
                              <select
                                className="w-full rounded-xl border border-slate-300/70 bg-white/90 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                                value={createTplBaseId}
                                onChange={(e) => setCreateTplBaseId(e.target.value)}
                              >
                                <option value="">-- start from current draft --</option>
                                {templates.map((tpl) => (
                                  <option key={tpl.id} value={tpl.id}>
                                    {tpl.name}
                                  </option>
                                ))}
                              </select>
                              <p className="text-xs text-slate-500 mt-1">
                                If selected, we’ll load that template into the editor first, then save as new.
                              </p>
                            </div>
                          </div>
                        </DialogContent>

                        <DialogActions sx={{ p: 2 }}>
                          <button className="btn-secondary" type="button" onClick={() => setCreateTplOpen(false)} disabled={tplBusy}>
                            Cancel
                          </button>
                          <button
                            className={`btn-primary ${tplBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                            type="button"
                            onClick={handleCreateTemplateConfirm}
                            disabled={tplBusy}
                          >
                            Create Template
                          </button>
                        </DialogActions>
                      </Dialog>
                    </div>
                  ) : (
                    <div className="rounded-2xl overflow-hidden border border-slate-300/70 bg-white/90">
                      <iframe title="email-preview-inline" className="w-full h-[720px]" srcDoc={currentPreview} />
                    </div>
                  )}
                </div>
              </div>
            )}

            {flowStep === 2 && (
              <div className="crd space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wide text-[#333999]">Review & Send</div>
                    <div className="text-xs text-slate-500 mt-1">Final checks before sending.</div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button type="button" className="btn-secondary" onClick={() => setFlowStep(1)}>
                      ← Back
                    </button>

                    <button
                      type="button"
                      className={`btn-primary ${reviewChecklist.issues.length ? "opacity-60 cursor-not-allowed" : ""}`}
                      disabled={!!reviewChecklist.issues.length || sending}
                      onClick={() => {
                        setPendingAction("sendSelected");
                        setShowConfirmModal(true);
                      }}
                    >
                      {sending ? `Sending ${progress.sent}/${progress.total} (${sendProgressPct}%)` : "Send Campaign"}
                    </button>
                  </div>
                </div>

                <div className="section-divider" />

                {sending ? (
                  <div className="rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3">
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <div className="font-semibold text-[#333999]">
                        Sending in progress: {progress.sent} of {progress.total} processed
                      </div>
                      <div className="font-bold text-[#333999]">{sendProgressPct}%</div>
                    </div>
                    <div className="mt-2 h-2 w-full rounded-full bg-indigo-100 overflow-hidden">
                      <div
                        className="h-full bg-[#333999] transition-all duration-300"
                        style={{ width: `${sendProgressPct}%` }}
                      />
                    </div>
                  </div>
                ) : null}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-slate-200 bg-white/80 p-4">
                    <div className="text-xs font-bold uppercase tracking-wide text-slate-600">Campaign Summary</div>
                    <div className="mt-3 space-y-2 text-sm">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-slate-500">Audience</span>
                        <span className="font-bold text-slate-800">{selectedCategory || "—"}</span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-slate-500">Subject</span>
                        <span className="font-bold text-slate-800 truncate max-w-[240px]" title={subject}>
                          {subject || "—"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-slate-500">From</span>
                        <span className="font-bold text-slate-800 truncate max-w-[240px]" title={`${fromName} <${fromEmail}>`}>
                          {fromName} &lt;{fromEmail}&gt;
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-slate-500">Selected</span>
                        <span className="font-bold text-slate-800">{selectedCount}</span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-slate-500">Will Send</span>
                        <span className="font-bold text-slate-800">{reviewChecklist.toSendCount}</span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-slate-500">Release</span>
                        <span className="font-bold text-slate-800">{releaseDate}</span>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={async () => {
                          try {
                            await ensureExportFresh();
                            alert("Builder exported.");
                          } catch (e) {
                            alert(e?.message || "Builder not ready.");
                          }
                        }}
                      >
                        Export
                      </button>
                      <button type="button" className="btn-secondary" onClick={() => setFlowStep(1)}>
                        Edit Content
                      </button>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white/80 p-4">
                    <div className="text-xs font-bold uppercase tracking-wide text-slate-600">Checklist</div>

                    <div className="mt-3 space-y-2 text-sm">
                      {reviewChecklist.issues.length ? (
                        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-red-700">
                          <div className="font-extrabold mb-1">Fix before sending</div>
                          <ul className="list-disc pl-5">
                            {reviewChecklist.issues.map((x, i) => (
                              <li key={i}>{x}</li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <div className="rounded-xl border border-green-200 bg-green-50 p-3 text-green-800">
                          <div className="font-extrabold">All required checks passed ✓</div>
                          <div className="text-xs mt-1">You can send the campaign.</div>
                        </div>
                      )}

                      {reviewChecklist.warnings.length ? (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-800">
                          <div className="font-extrabold mb-1">Warnings</div>
                          <ul className="list-disc pl-5">
                            {reviewChecklist.warnings.map((x, i) => (
                              <li key={i}>{x}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>

                    <div className="mt-4">
                      <div className="text-xs text-slate-500 mb-2">Quick actions</div>
                      <div className="flex flex-wrap gap-2">
                        <button type="button" className="btn-secondary" onClick={() => setFlowStep(0)}>
                          Edit Audience
                        </button>
                        <button type="button" className="btn-secondary" onClick={() => setFlowStep(1)}>
                          Edit Content
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="section-divider" />

                <div className="rounded-2xl overflow-hidden border border-slate-300/70 bg-white/90">
                  <iframe title="email-preview-review" className="w-full h-[560px]" srcDoc={currentPreview} />
                </div>
              </div>
            )}
          </div>
        </div>

        {showConfirmModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 p-3">
            <div className="dialog-shell w-full max-w-md">
              <div className="dialog-header">
                <div className="font-bold">Confirm Send</div>
              </div>

              <div className="p-5 bg-white/80">
                <p className="text-sm text-slate-700">
                  Send to <span className="font-bold text-[#333999]">{reviewChecklist.toSendCount}</span> recipient(s)? (Selected: {selectedCount})
                </p>

                {reviewChecklist.issues.length ? (
                  <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">Fix checklist issues before sending.</div>
                ) : null}
              </div>

              <div className="dialog-actions-bar">
                <button
                  className="btn-secondary"
                  type="button"
                  onClick={() => {
                    setShowConfirmModal(false);
                    setPendingAction(null);
                  }}
                >
                  Cancel
                </button>

                <button
                  className={`btn-primary ${reviewChecklist.issues.length ? "opacity-60 cursor-not-allowed" : ""}`}
                  disabled={reviewChecklist.issues.length}
                  type="button"
                  onClick={() => {
                    setShowConfirmModal(false);
                    if (pendingAction === "sendSelected") sendSelectedContacts();
                    setPendingAction(null);
                  }}
                >
                  Confirm Send
                </button>
              </div>
            </div>
          </div>
        )}

        {notesDialogOpen && activeContact && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 p-3">
            <div className="dialog-shell w-full max-w-2xl max-h-[85vh] flex flex-col">
              <div className="dialog-header">
                <div className="font-bold">Notes for {activeContact.company || activeContact.name || ""}</div>
              </div>

              <div className="p-5 bg-white/80 flex-1 overflow-hidden flex flex-col gap-3">
                <div className="flex-1 overflow-auto rounded-2xl border border-slate-300/70 bg-white/90 p-3">
                  {(activeContact.notes || []).length === 0 ? (
                    <p className="text-xs text-slate-400">No notes yet. Add your first note below.</p>
                  ) : (
                    <ul className="space-y-2 text-xs">
                      {activeContact.notes.map((n, i) => (
                        <li key={i} className="border-b border-slate-100 pb-2 last:border-none">
                          <div className="text-slate-800 mb-1">{n.note}</div>
                          <div className="text-slate-500 text-[11px]">{new Date(n.time).toLocaleString()}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <textarea
                  className="w-full rounded-2xl border border-slate-300/70 bg-white/90 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                  rows={3}
                  placeholder="Add a note..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                />

                <div className="flex justify-end gap-3">
                  <button
                    className="btn-secondary"
                    type="button"
                    onClick={() => {
                      setNotesDialogOpen(false);
                      setActiveContact(null);
                      setNewNote("");
                    }}
                  >
                    Close
                  </button>

                  <button className={`btn-primary ${!newNote.trim() ? "opacity-60 cursor-not-allowed" : ""}`} type="button" onClick={saveNote} disabled={!newNote.trim()}>
                    Save Note
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {userClicksOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 p-3">
            <div className="dialog-shell w-full max-w-6xl max-h-[90vh] flex flex-col">
              <div className="dialog-header flex items-center justify-between gap-3">
                <div className="font-bold">User Clicks {selectedCategory ? `— ${selectedCategory}` : ""}</div>
                <button type="button" className="btn-secondary btn-sm" onClick={closeUserClicksDialog}>
                  Close
                </button>
              </div>

              <div className="p-4 bg-white/80 flex-1 overflow-hidden flex flex-col gap-3">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <label className="section-label">Filter by Subject</label>
                    <select
                      className="w-full rounded-xl border border-slate-300/70 bg-white/90 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                      value={userClicksSubjectFilter}
                      onChange={(e) => setUserClicksSubjectFilter(e.target.value)}
                    >
                      <option value="all">All Subjects</option>
                      {(userClicksFilterOptions.subjects || []).map((s, i) => (
                        <option key={`${s}-${i}`} value={String(s).toLowerCase()}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="section-label">Filter by Clicked Link</label>
                    <select
                      className="w-full rounded-xl border border-slate-300/70 bg-white/90 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                      value={userClicksLinkFilter}
                      onChange={(e) => setUserClicksLinkFilter(e.target.value)}
                    >
                      <option value="all">All Links</option>
                      {(userClicksFilterOptions.links || []).map((u, i) => (
                        <option key={`${u}-${i}`} value={String(u).toLowerCase()}>
                          {u}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="section-label">Search Email</label>
                    <input
                      value={userClicksEmailSearch}
                      onChange={(e) => setUserClicksEmailSearch(e.target.value)}
                      className="w-full rounded-xl border border-slate-300/70 bg-white/90 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                      placeholder="name@example.com"
                    />
                  </div>

                  <div className="flex items-end">
                    <button type="button" className="btn-secondary w-full" onClick={fetchUserClicks} disabled={userClicksBusy}>
                      {userClicksBusy ? "Loading…" : "Refresh"}
                    </button>
                  </div>
                </div>

                {userClicksError ? (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{userClicksError}</div>
                ) : null}

                <div className="text-xs text-slate-500">
                  Showing <span className="font-bold text-slate-700">{filteredUserClicksRows.length}</span> email(s).
                </div>

                <div className="flex-1 overflow-auto rounded-2xl border border-slate-300/70 bg-white/90">
                  <table className="min-w-full text-xs">
                    <thead className="sticky top-0 z-10 bg-gradient-to-r from-slate-50 to-indigo-50 border-b border-slate-200">
                      <tr className="text-slate-700">
                        <th className="px-3 py-2 text-left font-bold text-[#333999]">Details</th>
                        <th className="px-3 py-2 text-left font-bold text-[#333999]">Email</th>
                        <th className="px-3 py-2 text-left font-bold text-[#333999]">Last Clicked</th>
                        <th className="px-3 py-2 text-left font-bold text-[#333999]">Total Clicks</th>
                        <th className="px-3 py-2 text-left font-bold text-[#333999]">Last Subject</th>
                        <th className="px-3 py-2 text-left font-bold text-[#333999]">Last Link</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {userClicksBusy && !filteredUserClicksRows.length ? (
                        <tr>
                          <td colSpan={6} className="px-3 py-6 text-center text-slate-400">
                            Loading click activity...
                          </td>
                        </tr>
                      ) : null}

                      {!userClicksBusy && !filteredUserClicksRows.length ? (
                        <tr>
                          <td colSpan={6} className="px-3 py-6 text-center text-slate-400">
                            No click activity found for this filter.
                          </td>
                        </tr>
                      ) : null}

                      {filteredUserClicksRows.map((row) => {
                        const expanded = !!userClicksExpandedRows[row.id];
                        const eventsBusy = !!userClickEventsBusyByRow[row.id];
                        const events = userClickEventsByRow[row.id] || [];
                        const lastClickedMs = toMillisFromUnknownTime(row.lastClickedAt) || toMillisFromUnknownTime(row.lastClickedTimestamp);
                        return (
                          <React.Fragment key={row.id}>
                            <tr className="bg-white/80 hover:bg-indigo-50/60 transition-colors">
                              <td className="px-3 py-2 align-top">
                                <button
                                  type="button"
                                  className="text-indigo-700 underline font-semibold"
                                  onClick={() => toggleUserClicksRow(row.id)}
                                >
                                  {expanded ? "Hide" : "Expand"}
                                </button>
                              </td>
                              <td className="px-3 py-2 align-top break-all">{row.email}</td>
                              <td className="px-3 py-2 align-top">{lastClickedMs ? formatShortDateTime(lastClickedMs) : "-"}</td>
                              <td className="px-3 py-2 align-top">{Number(row.totalClicks || 0)}</td>
                              <td className="px-3 py-2 align-top">{row.lastSubject || "-"}</td>
                              <td className="px-3 py-2 align-top break-all">
                                {row.lastClickedUrl ? (
                                  <a href={row.lastClickedUrl} target="_blank" rel="noreferrer" className="text-indigo-700 underline">
                                    {row.lastClickedUrl}
                                  </a>
                                ) : (
                                  "-"
                                )}
                              </td>
                            </tr>
                            {expanded ? (
                              <tr className="bg-indigo-50/30">
                                <td colSpan={6} className="px-3 py-3">
                                  {eventsBusy ? (
                                    <div className="text-xs text-slate-500">Loading click events...</div>
                                  ) : !events.length ? (
                                    <div className="text-xs text-slate-500">No click events found for this email.</div>
                                  ) : (
                                    <div className="space-y-2">
                                      {events.map((evt) => {
                                        const evtMs = toMillisFromUnknownTime(evt.at) || toMillisFromUnknownTime(evt.timestamp);
                                        return (
                                          <div key={evt.id} className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                                            <div className="text-[11px] text-slate-500">{evtMs ? formatShortDateTime(evtMs) : "-"}</div>
                                            <div className="text-xs text-slate-800 break-all">
                                              <span className="font-semibold">URL:</span>{" "}
                                              <a href={evt.url} target="_blank" rel="noreferrer" className="text-indigo-700 underline">
                                                {evt.url}
                                              </a>
                                            </div>
                                            <div className="text-xs text-slate-700 mt-1">
                                              <span className="font-semibold">Subject:</span> {evt.subject || "-"}{" "}
                                              <span className="mx-1">•</span>
                                              <span className="font-semibold">Type:</span> {evt.linkType || "other"}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ) : null}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        <Dialog
          open={intakeModalOpen}
          onClose={closeIntakeModal}
          fullWidth
          maxWidth="md"
          keepMounted={false}
          sx={{
            "& .MuiDialog-paper": {
              margin: { xs: "16px", sm: "32px" },
              width: { xs: "calc(100% - 32px)", sm: "100%" },
              borderRadius: "1.25rem",
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 900 }}>Intake Form {intakeEmail ? `— ${intakeEmail}` : ""}</DialogTitle>
          <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2 } }}>
            <IntakeForm email={intakeEmail} />
          </DialogContent>
          <DialogActions>
            <button className="bg-primary py-2 px-4 rounded-md text-white ml-2" onClick={closeIntakeModal}>
              Close
            </button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default MarketingEmail;
